import { NoSsr } from "@mui/base";
import { useTheme } from "@mui/material/styles";
import { usePrevious } from "@react-hookz/web";
import { useScroll } from "ahooks";
import { useAtomValue } from "jotai";
import type { PageProps } from "next";
import { useRouter } from "next/router";
import type { CSSProperties, FunctionComponent, ReactNode } from "react";
import { useMemo } from "react";

import { useResponsiveAdSlot } from "~/components/advertisement/hooks";
import { appBarScrollStickyAtom } from "~/components/app-bar/hooks/sticky/atoms";
import { HeaderBottomContainerElementID } from "~/pages/_app/consts";
import { pathname as homepagePathname } from "~/pages/home";
import { getHomepageScrollY } from "~/pages/home/helpers";

import { appBarAtom } from "./atoms";
import { HideElementOnScrollEffectThreshold } from "./consts";
import { AppBarContextProvider } from "./contexts";
import type { AppBarVariant } from "./helpers";
import { useAppBarSetStickyHeight } from "./hooks";
import {
  AdSlotContainer,
  Container,
  FullWidthBackground,
  HeaderBottomContainer,
  StyledHeader,
  StyledHomeHeader,
  StyledHomeMiniHeader,
  StyledPlusHeader,
  StyledPostiesBrochureHeader,
  StyledPostiesContentHeader,
  StyledStyleContentHeader,
} from "./styles";

export type Props = {
  children?: ReactNode;
  className?: string;
  hideTargetChildren?: ReactNode;
  pageProps?: PageProps;
};

export const AppBar: FunctionComponent<Props> = ({ children, className, pageProps }) => {
  const { adSlotHeight, bindAdSlotContainer, responsiveAdSlot, stickyStyle } = useResponsiveAdSlot(
    pageProps?.appBarConfiguration,
  );

  const appBarValue = useAtomValue(appBarAtom);
  const { stickyScrollHeight = 0 } = useAtomValue(appBarScrollStickyAtom);
  const appBarStickyScrollHeight = useMemo(
    // added 2 trying to avoid double menu when scrolling up/down, should find a better way.
    () => stickyScrollHeight + adSlotHeight,
    [adSlotHeight, stickyScrollHeight],
  );

  const appBarVariant = useMemo<AppBarVariant>(
    () =>
      appBarValue?.scrollOverVariant ??
      pageProps?.appBarConfiguration?.variant ??
      "scmp/generic-light",
    [appBarValue?.scrollOverVariant, pageProps?.appBarConfiguration?.variant],
  );

  const hideElementOnScrollEffect = useStickyWithHideElementAfterScrollThreshold(
    HideElementOnScrollEffectThreshold.mobile,
    appBarStickyScrollHeight,
  );
  const stickyWithScrollAwayElement = useStickyWithScrollAwayElement(appBarStickyScrollHeight);

  const styles: CSSProperties = {
    ...(stickyStyle === "withHideElementAfterScrollThreshold"
      ? hideElementOnScrollEffect.targetContainerStyles
      : stickyWithScrollAwayElement.targetContainerStyles),
  };

  const { reference } = useAppBarSetStickyHeight();

  const homeHeaderConfiguration = {
    ...pageProps?.headerConfiguration,
    appBarStickyScrollHeight,
    appBarVariant,
  };

  const renderHeader = () => {
    switch (appBarVariant) {
      case "posties/generic":
        return <StyledPostiesContentHeader />;
      case "posties/brochure":
        return <StyledPostiesBrochureHeader />;
      case "scmp/magazines-style":
        const headerConfiguration = {
          ...pageProps?.headerConfiguration,
          appBarStickyScrollHeight,
          appBarVariant,
        };
        return <StyledStyleContentHeader headerConfiguration={headerConfiguration} />;
      case "scmp/home":
        return <StyledHomeHeader headerConfiguration={homeHeaderConfiguration} />;
      case "scmp/home-slim":
        return <StyledHomeMiniHeader headerConfiguration={homeHeaderConfiguration} />;
      case "scmp/plus":
      case "scmp/plus/learnmore":
        return <StyledPlusHeader headerConfiguration={homeHeaderConfiguration} />;
      default:
        return <StyledHeader headerConfiguration={pageProps?.headerConfiguration} />;
    }
  };

  const renderLayout = () =>
    appBarVariant === "scmp/home" ? (
      <>
        {children}
        {renderHeader()}
        <AdSlotContainer {...bindAdSlotContainer}>
          <NoSsr>{responsiveAdSlot}</NoSsr>
        </AdSlotContainer>
      </>
    ) : (
      <>
        <AdSlotContainer {...bindAdSlotContainer}>
          <NoSsr>{responsiveAdSlot}</NoSsr>
        </AdSlotContainer>
        {children}
        {renderHeader()}
      </>
    );

  return (
    <AppBarContextProvider>
      <Container
        $appBarVariant={appBarVariant}
        className={className}
        ref={reference}
        style={{ ...styles }}
      >
        <FullWidthBackground />
        {renderLayout()}
        <HeaderBottomContainer id={HeaderBottomContainerElementID} />
      </Container>
    </AppBarContextProvider>
  );
};

AppBar.displayName = "AppBar";

export const useShouldHidden = (scrollThreshold: number) => {
  const router = useRouter();
  const isHomepage = router.pathname === homepagePathname;

  const position = useScroll();

  if (isHomepage && !position) {
    return null;
  }

  const defaultScrollOffsetY = position?.top ?? 0;

  const containerElementScrollOffsetY =
    isHomepage && defaultScrollOffsetY === 0 ? getHomepageScrollY() ?? 0 : defaultScrollOffsetY;
  return containerElementScrollOffsetY > scrollThreshold;
};

// This effect will hide the element with transition after the scroll container scroll passes a threshold
const useStickyWithHideElementAfterScrollThreshold = (
  scrollThreshold: number,
  targetElementHeight: number,
) => {
  const theme = useTheme();
  const previousTargetElementHeight = usePrevious(targetElementHeight);

  const shouldHidden = useShouldHidden(scrollThreshold);
  const shouldPauseTransition = previousTargetElementHeight !== targetElementHeight;

  const targetContainerStyles: CSSProperties = {
    position: "sticky",
    transform: shouldHidden ? `translateY(-${targetElementHeight}px)` : "none",
    transition: shouldPauseTransition
      ? "none" // eslint-disable-next-line etc/no-internal
      : theme.transitions.create("transform", {
          duration: 50,
        }),
  };

  return {
    targetContainerStyles,
  };
};

// This effect will make the container sticky after the scroll away element is complete scroll
// passed in the scroll container
const useStickyWithScrollAwayElement = (scrollAwayElementHeight: number) => {
  const position = useScroll();
  const containerElementScrollOffsetY = position?.top ?? 0;

  const router = useRouter();
  const isHomepage = router.pathname === homepagePathname;

  const shouldStick =
    containerElementScrollOffsetY > 0 && containerElementScrollOffsetY >= scrollAwayElementHeight;

  const targetContainerStyles: CSSProperties = isHomepage
    ? {
        position: "relative",
      }
    : {
        position: shouldStick ? "sticky" : "relative",
        transform: shouldStick ? `translateY(-${scrollAwayElementHeight}px)` : "none",
      };

  return {
    targetContainerStyles,
  };
};
